import { Sparkles, Star } from "lucide-react";
import { site } from "@/data/siteContent";

const ZODIAC_URL =
  "https://images.unsplash.com/photo-1729335511904-9b8690184935?auto=format&fit=crop&w=1200&q=80";
const STARFIELD_URL =
  "https://images.unsplash.com/photo-1472552944129-b035e9ea3744?auto=format&fit=crop&w=1600&q=70";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-32 pb-24 overflow-hidden cosmic-bg"
    >
      <div className="stars" aria-hidden />

      {/* Ambient starfield behind everything */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(${STARFIELD_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />
      {/* Faint spinning zodiac wheel on top-right */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-40 w-[720px] h-[720px] opacity-[0.18] animate-slow-spin"
        style={{
          backgroundImage: `url(${ZODIAC_URL})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-56 bottom-[-260px] w-[640px] h-[640px] opacity-[0.10] animate-slow-spin-reverse"
        style={{
          backgroundImage: `url(${ZODIAC_URL})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          mixBlendMode: "screen",
        }}
      />

      <div className="container-luxe relative grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
        {/* LEFT copy */}
        <div className="reveal">
          <div className="ornament mb-8">
            <span className="text-[11px] tracking-[0.4em] uppercase text-gold-light">
              Since 1998 · वैदिक ज्योतिष · Surat, Gujarat
            </span>
          </div>
          <h1
            data-testid="hero-title"
            className="font-display text-[40px] sm:text-[54px] lg:text-[68px] leading-[1.03] text-cream"
          >
            <span className="block">Best</span>
            <span className="block">
              <span className="italic font-editorial gold-text">Astrologer</span>{" "}
              Services
            </span>
            <span className="block">
              in <span className="gold-text">Gujarat</span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-lg text-cream leading-relaxed"
          >
            Trusted Astrology, Vashikaran, Love, Marriage and Spiritual Guidance
            by{" "}
            <span className="text-gold font-medium">{site.brand.name}</span>.
          </p>

          <p className="font-devanagari text-base sm:text-lg mt-4 text-gold-light/90">
            {site.brand.nameHi} — गुजरात के अग्रणी वैदिक ज्योतिषी
          </p>

          <p className="mt-5 max-w-xl text-cream-muted text-[14.5px] leading-relaxed">
            Two and a half decades of authentic Vedic wisdom, delivered with
            clarity, compassion and complete confidentiality — for seekers
            across Gujarat and the world.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#booking"
              data-testid="hero-book-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#D4AF37] text-[#050B14] font-semibold text-[13px] tracking-[0.2em] uppercase hover:bg-[#F3E5AB] transition-colors duration-500 gold-glow"
            >
              <Sparkles className="w-4 h-4" />
              Book Consultation
            </a>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="inline-flex items-center px-8 py-3.5 rounded-full gold-border text-gold text-[13px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-500"
            >
              Explore Services
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
            {[
              { n: "25+", l: "Years Experience" },
              { n: "10K+", l: "Consultations" },
              { n: "4.9★", l: "Client Rating" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl gold-text">{s.n}</div>
                <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-cream-muted">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Vedic Astrology emblem (no portrait) */}
        <div className="reveal relative flex justify-center lg:justify-end">
          <div className="relative w-full max-w-[520px] aspect-square">
            {/* Outer glow */}
            <div
              aria-hidden
              className="absolute -inset-4 rounded-full bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.35),transparent_70%)] blur-2xl"
            />
            {/* Rotating zodiac disk */}
            <div className="absolute inset-0 rounded-full overflow-hidden gold-border">
              <div
                className="absolute inset-0 animate-slow-spin"
                style={{
                  backgroundImage: `url(${ZODIAC_URL})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "sepia(0.35) hue-rotate(-10deg) saturate(1.15) brightness(0.85)",
                }}
              />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(5,11,20,0.9)_75%)]" />
            </div>

            {/* Inner rings */}
            <div
              aria-hidden
              className="absolute inset-8 rounded-full border border-[#D4AF37]/30 animate-slow-spin-reverse"
            />
            <div
              aria-hidden
              className="absolute inset-16 rounded-full border border-[#D4AF37]/20 animate-slow-spin"
            />

            {/* Center Om + shloka */}
            <div className="absolute inset-0 grid place-items-center text-center px-8">
              <div>
                <div
                  className="font-devanagari text-gold text-7xl sm:text-8xl leading-none"
                  style={{
                    textShadow:
                      "0 0 40px rgba(212,175,55,0.6), 0 0 12px rgba(212,175,55,0.4)",
                  }}
                >
                  ॐ
                </div>
                <div className="mt-4 font-display text-[11px] tracking-[0.35em] text-gold-light">
                  ॥ जय अम्बे ॥
                </div>
                <p className="mt-5 font-editorial italic text-cream-muted text-[13.5px] max-w-[240px] mx-auto leading-relaxed">
                  “Yatha pinde tatha brahmande” <br />
                  <span className="text-[11px] text-cream-muted/70">
                    as within, so without
                  </span>
                </p>
              </div>
            </div>

            {/* Floating gold sparkles */}
            <div className="absolute -top-2 left-6 w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_16px_#D4AF37]" />
            <div className="absolute top-16 -right-1 w-1.5 h-1.5 rounded-full bg-[#F3E5AB] shadow-[0_0_12px_#F3E5AB]" />
            <div className="absolute bottom-8 left-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_12px_#D4AF37]" />
          </div>

          {/* Small floating chip */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 lg:left-auto lg:right-4 lg:translate-x-0 glass px-5 py-3 rounded-2xl flex items-center gap-3 shadow-xl">
            <Star className="w-4 h-4 text-gold fill-[#D4AF37]" />
            <div className="text-[11px] tracking-[0.22em] uppercase text-cream">
              Trusted since 1998
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#050B14]" />
    </section>
  );
}
